import React, { useState, useEffect } from "react";
import Lightbox from "yet-another-react-lightbox";
import Captions from "yet-another-react-lightbox/plugins/captions";
import "yet-another-react-lightbox/styles.css";
import "yet-another-react-lightbox/plugins/captions.css";

import "./RepresentantesComponent.css";
import ImageWithLoader from "../ImageWithLoader";

const API_BASE = "https://api.fallaplazarodrigo.es:999";

export const RepresentantesComponent = (props) => {
  const [page, setPage] = useState(1);
  const itemsPerPage = 8;
  const totalPages = Math.ceil((props.Data?.length || 0) / itemsPerPage);
  const [yearRanges, setYearRanges] = useState([]);

  // estado del lightbox
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxSlides, setLightboxSlides] = useState([]);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  useEffect(() => {
    updateYearRanges();
  }, [props.Data, totalPages]);

  const updateYearRanges = () => {
    if (!props.Data || props.Data.length === 0) {
      setYearRanges([]);
      return;
    }

    const ranges = [];

    for (let i = 1; i <= totalPages; i++) {
      const startIndex = (i - 1) * itemsPerPage;
      if (startIndex >= props.Data.length) break;

      const endIndex = Math.min(startIndex + itemsPerPage, props.Data.length);
      const startYear = props.Data[startIndex]?.year;
      const endYear = props.Data[endIndex - 1]?.year;

      ranges.push(`${startYear} - ${endYear}`);
    }

    setYearRanges(ranges);
  };

  const handlePageChange = (pageNumber) => {
    setPage(pageNumber);
  };

  const getDisplayYear = (year) => {
    return year === 2021 ? "2020/2021" : year;
  };

  const getImageUrl = (url) => {
    if (!url) return "";
    return url.startsWith("http") ? url : `${API_BASE}${url}`;
  };

  // render de nombres con saltos de línea
  const renderMultiline = (text) => {
    if (!text && text !== "") return null;
    if (typeof text !== "string") return text;

    const normalized = text
      .replace(/\\r\\n/g, "\n")
      .replace(/\\r/g, "\n")
      .replace(/\\n/g, "\n");

    if (!normalized.includes("\n")) return normalized;

    const parts = normalized.split("\n");
    return parts.map((part, i) => (
      <span key={i}>
        {part}
        {i < parts.length - 1 && <br />}
      </span>
    ));
  };

  // construye los slides del lightbox para un año
  const buildSlidesForYear = (representantes) => {
    if (!representantes) return [];

    const year = getDisplayYear(representantes.year);

    const roles = [
      { key: "presidente", label: props.I18n.President },
      { key: "falleraMayor", label: props.I18n.FalleraMayor },
      { key: "presidenteInfantil", label: props.I18n.ChildrensPresident },
      {
        key: "falleraMayorInfantil",
        label: props.I18n.ChildrensFalleraMayor,
      },
    ];

    const slides = [];

    roles.forEach(({ key, label }) => {
      const rep = representantes[key];
      if (rep && rep.image) {
        slides.push({
          src: getImageUrl(rep.image),
          alt: rep.nombre,
          title: rep.nombre,
          description: `${label} ${year}`,
          _roleKey: key, // campo interno para encontrar índice
        });
      }
    });

    return slides;
  };

  const openLightbox = (slides, index) => {
    if (!slides || slides.length === 0) return;
    setLightboxSlides(slides);
    setLightboxIndex(index >= 0 ? index : 0);
    setLightboxOpen(true);
  };

  const pageNumbers = [];
  for (let i = 1; i <= totalPages; i++) {
    pageNumbers.push(i);
  }

  return (
    <div className="RepresentantesComponent">
      {/* Lightbox global */}
      <Lightbox
        className="fp-lightbox"
        open={lightboxOpen}
        close={() => setLightboxOpen(false)}
        slides={lightboxSlides}
        index={lightboxIndex}
        plugins={[Captions]}
        // un pelín más “app”
        animation={{ swipe: 300 }}
        carousel={{ finite: false, padding: 0, spacing: 0 }}
      />

      <div className="paginado">
        {pageNumbers.map((pageNumber, index) => (
          <button
            key={pageNumber}
            onClick={() => handlePageChange(pageNumber)}
            className={pageNumber === page ? "selected" : ""}
          >
            ({yearRanges[index] || ""})
          </button>
        ))}
      </div>

      <div className="all-Representantes">
        {props.Data
          ?.slice((page - 1) * itemsPerPage, page * itemsPerPage)
          .map((representantes, index) => {
            const slidesForYear = buildSlidesForYear(representantes);

            const findIndexByRole = (roleKey) =>
              slidesForYear.findIndex((s) => s._roleKey === roleKey);

            return (
              <div key={index} className="Representantes-content">
                <h2>{getDisplayYear(representantes.year)}</h2>
                <hr />
                <div className="Representantes-images">
                  {representantes.presidente &&
                    representantes.falleraMayor && (
                      <div className="representantes">
                        <h2>{props.I18n.Representatives}</h2>
                        <hr />
                        <div>
                          {representantes.presidente && (
                            <div
                              className="representante"
                              role="button"
                              tabIndex={0}
                              onClick={() =>
                                openLightbox(
                                  slidesForYear,
                                  findIndexByRole("presidente")
                                )
                              }
                            >
                              <ImageWithLoader
                                src={getImageUrl(
                                  representantes.presidente.imagenMin
                                )}
                                alt={representantes.presidente.nombre}
                              />
                              <h4>
                                {renderMultiline(
                                  representantes.presidente.nombre
                                )}
                              </h4>
                              <p>
                                {props.I18n.President}{" "}
                                {getDisplayYear(representantes.year)}
                              </p>
                            </div>
                          )}

                          {representantes.falleraMayor && (
                            <div
                              className="representante"
                              role="button"
                              tabIndex={0}
                              onClick={() =>
                                openLightbox(
                                  slidesForYear,
                                  findIndexByRole("falleraMayor")
                                )
                              }
                            >
                              <ImageWithLoader
                                src={getImageUrl(
                                  representantes.falleraMayor.imagenMin
                                )}
                                alt={representantes.falleraMayor.nombre}
                              />
                              <h4>
                                {renderMultiline(
                                  representantes.falleraMayor.nombre
                                )}
                              </h4>
                              <p>
                                {props.I18n.FalleraMayor}{" "}
                                {getDisplayYear(representantes.year)}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                  {(representantes.presidenteInfantil ||
                    representantes.falleraMayorInfantil) && (
                    <div className="representantes">
                      <h2>{props.I18n.ChildrensRepresentatives}</h2>
                      <hr />
                      <div>
                        {representantes.presidenteInfantil && (
                          <div
                            className="representante"
                            role="button"
                            tabIndex={0}
                            onClick={() =>
                              openLightbox(
                                slidesForYear,
                                findIndexByRole("presidenteInfantil")
                              )
                            }
                          >
                            <ImageWithLoader
                              src={getImageUrl(
                                representantes.presidenteInfantil.imagenMin
                              )}
                              alt={representantes.presidenteInfantil.nombre}
                            />
                            <h4>
                              {renderMultiline(
                                representantes.presidenteInfantil.nombre
                              )}
                            </h4>
                            <p>
                              {props.I18n.ChildrensPresident}{" "}
                              {getDisplayYear(representantes.year)}
                            </p>
                          </div>
                        )}

                        {representantes.falleraMayorInfantil && (
                          <div
                            className="representante"
                            role="button"
                            tabIndex={0}
                            onClick={() =>
                              openLightbox(
                                slidesForYear,
                                findIndexByRole("falleraMayorInfantil")
                              )
                            }
                          >
                            <ImageWithLoader
                              src={getImageUrl(
                                representantes.falleraMayorInfantil.imagenMin
                              )}
                              alt={representantes.falleraMayorInfantil.nombre}
                            />
                            <h4>
                              {renderMultiline(
                                representantes.falleraMayorInfantil.nombre
                              )}
                            </h4>
                            <p>
                              {props.I18n.ChildrensFalleraMayor}{" "}
                              {getDisplayYear(representantes.year)}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                <hr />
              </div>
            );
          })}
      </div>
    </div>
  );
};
